/**
 * Redact contact-info matches for safety logs. Never keep the full detail.
 * Categories: phone, email, handle/social, address, payment.
 */

export const CONTACT_FLAG_CATEGORIES = ['phone', 'email', 'handle/social', 'address', 'payment'];

const SIGNAL_TO_FLAG = {
  phone: 'phone',
  email: 'email',
  socials: 'handle/social',
  url: 'handle/social',
  address: 'address',
  payment: 'payment',
  payment_details: 'payment',
};

const DIGIT_WORDS = {
  zero: '0',
  oh: '0',
  o: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
};

/**
 * Map a detector signal to the Safety review category.
 *
 * @param {string} [signal]
 * @returns {string|null}
 */
export const contactFlagCategory = signal => SIGNAL_TO_FLAG[signal] || null;

/**
 * First Safety category from a list of detector signals.
 *
 * @param {string[]} [signals]
 * @returns {string|null}
 */
export const firstContactFlagCategory = (signals = []) => {
  const list = Array.isArray(signals) ? signals : [];
  for (let i = 0; i < list.length; i += 1) {
    const mapped = contactFlagCategory(list[i]);
    if (mapped) {
      return mapped;
    }
  }
  return null;
};

const digitsFromSpelledOut = text => {
  const words = String(text || '')
    .toLowerCase()
    .match(/[a-z]+/g);
  if (!words) {
    return '';
  }
  let digits = '';
  words.forEach(word => {
    if (DIGIT_WORDS[word] != null) {
      digits += DIGIT_WORDS[word];
    } else if (digits.length > 0 && digits.length < 7) {
      digits = '';
    }
  });
  return digits.replace(/\D/g, '');
};

const phoneDigits = text => {
  const raw = String(text || '').replace(/\D/g, '');
  if (raw.length >= 10) {
    return raw.slice(-10);
  }
  const spelled = digitsFromSpelledOut(text);
  return spelled.length >= 10 ? spelled.slice(-10) : raw;
};

const redactPhone = text => {
  const digits = phoneDigits(text);
  if (digits.length >= 10) {
    return `${digits.slice(0, 3)}-***-****`;
  }
  return '[phone]';
};

const redactEmail = text => {
  const match = String(text || '').match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  if (!match) {
    return '[email]';
  }
  const [local] = match[0].split('@');
  const first = local.slice(0, 1) || 'x';
  return `${first}***@***.com`;
};

const redactHandle = text => {
  const at = String(text || '').match(/@([a-z0-9._]{2,30})/i);
  if (at) {
    return `@${at[1].slice(0, 1)}***`;
  }
  return '[handle]';
};

/**
 * Redacted snippet for a blocked contact match. Never the full value.
 *
 * @param {string} [text]
 * @param {string} [categoryOrSignal]
 * @returns {string}
 */
export const redactContactSnippet = (text, categoryOrSignal) => {
  const category = CONTACT_FLAG_CATEGORIES.includes(categoryOrSignal)
    ? categoryOrSignal
    : contactFlagCategory(categoryOrSignal) || firstContactFlagCategory([categoryOrSignal]);
  if (category === 'phone') {
    return redactPhone(text);
  }
  if (category === 'email') {
    return redactEmail(text);
  }
  if (category === 'handle/social') {
    return redactHandle(text);
  }
  if (category === 'address') {
    return '[address]';
  }
  if (category === 'payment') {
    return '[payment]';
  }
  return '[redacted]';
};

/**
 * True when a stored snippet looks redacted, not a raw phone or email.
 *
 * @param {string} [value]
 * @returns {boolean}
 */
export const isRedactedContactSnippet = value => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 80) {
    return false;
  }
  if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(trimmed)) {
    return false;
  }
  if (/\b\d{10,}\b/.test(trimmed) || /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(trimmed)) {
    return false;
  }
  return (
    /\*\*\*/.test(trimmed) ||
    /^\[[a-z/-]+\]$/.test(trimmed)
  );
};
